import React, { useContext } from 'react';
import { I18nReactContext } from '../../../../contexts/I18nContext';
import './CertificationStatusBadge.scss';

/**
 * Displays a color-coded badge for certification urgency status
 *
 * Status priority (most urgent first):
 * - EXPIRED: Certification has expired (red)
 * - EXPIRING_SOON: Certification expires within 90 days (orange)
 * - INCOMPLETE: Certification progress < 100% (blue)
 * - COMPLETED: All certifications complete and not expiring soon (green)
 *
 * @param {object} props - Component props
 * @param {string} props.status - One of: EXPIRED, EXPIRING_SOON, INCOMPLETE, COMPLETED
 * @returns {JSX.Element} Rendered badge component
 */
export const CertificationStatusBadge = ({ status }) => {
    const { i18n } = useContext(I18nReactContext);

    const statusConfig = {
        EXPIRED: {
            label: i18n.translate('certificationStatus.expired'),
            className: 'CertificationStatusBadge--expired'
        },
        EXPIRING_SOON: {
            label: i18n.translate('certificationStatus.expiringSoon'),
            className: 'CertificationStatusBadge--expiring-soon'
        },
        INCOMPLETE: {
            label: i18n.translate('certificationStatus.incomplete'),
            className: 'CertificationStatusBadge--incomplete'
        },
        COMPLETED: {
            label: i18n.translate('certificationStatus.completed'),
            className: 'CertificationStatusBadge--completed'
        }
    };

    // Default to INCOMPLETE if status is not recognized
    const config = statusConfig[status] || statusConfig.INCOMPLETE;

    return (
        <div
            className={`CertificationStatusBadge ${config.className}`}
            data-testid={`certification-status-badge-${status?.toLowerCase()}`}
        >
            <span className="CertificationStatusBadge__dot"></span>
            <span className="CertificationStatusBadge__label">{config.label}</span>
        </div>
    );
};
